import { z } from 'zod';
import { IdSchema } from '../../common';
// ============================================
// CMS — Public Site Content (hibrit CMS)
// ============================================
//
// Public tanıtım sitesi (apps/web) içeriği admin panelden yönetilir. İçerik
// "section" bazlı tek bir tabloda (site_content) JSON olarak saklanır ve her
// section bu şemalarla doğrulanır. Section'lar: 'settings', 'home', 'about'.
/** Düzenlenebilir site section anahtarları. */
export const SiteContentKeySchema = z.enum(['settings', 'home', 'about', 'pages']);
// ---- Ortak parçalar ----
export const CtaSchema = z.object({
    label: z.string().min(1).max(60),
    href: z.string().min(1).max(300),
});
// Menü / bağlantı öğesi (header navigasyonu + footer hızlı bağlantılar).
export const NavItemSchema = z.object({
    label: z.string().min(1).max(40),
    href: z.string().min(1).max(300),
});
export const SocialLinksSchema = z.object({
    facebook: z.string().max(300).default(''),
    instagram: z.string().max(300).default(''),
    youtube: z.string().max(300).default(''),
    twitter: z.string().max(300).default(''),
    whatsapp: z.string().max(300).default(''),
});
export const ContactInfoSchema = z.object({
    phone: z.string().max(40).default(''),
    email: z.string().max(160).default(''),
    address: z.string().max(240).default(''),
    mapEmbedUrl: z.string().max(600).default(''),
});
export const SeoSchema = z.object({
    titleSuffix: z.string().max(80).default('Gencay Akademi'),
    description: z.string().max(320).default(''),
});
// ---- Section: settings (genel site ayarları) ----
// Header navigasyonu (menü + giriş butonları + logo metni).
export const SiteNavigationSchema = z.object({
    items: z.array(NavItemSchema).max(8).default([]),
    studentLoginLabel: z.string().max(40).default('Öğrenci Girişi'),
    teacherLoginLabel: z.string().max(40).default('Öğretmen Girişi'),
});
const DEFAULT_NAVIGATION = {
    items: [
        { label: 'Anasayfa', href: '/' },
        { label: 'Eğitimler', href: '/egitimler' },
        { label: 'Öğretmenler', href: '/ogretmenler' },
        { label: 'Hakkımızda', href: '/hakkimizda' },
        { label: 'İletişim', href: '/iletisim' },
    ],
    studentLoginLabel: 'Öğrenci Girişi',
    teacherLoginLabel: 'Öğretmen Girişi',
};
// Footer içeriği (üst CTA şeridi + açıklama + hızlı bağlantılar + copyright).
export const SiteFooterSchema = z.object({
    ctaTitle: z.string().max(120).default('Geleceğe ilk adımı bugün at'),
    ctaText: z.string().max(240).default(''),
    ctaButton: CtaSchema,
    description: z.string().max(400).default(''),
    quickLinks: z.array(NavItemSchema).max(8).default([]),
    copyrightSuffix: z.string().max(120).default('Tüm hakları saklıdır.'),
});
const DEFAULT_FOOTER = {
    ctaTitle: 'Geleceğe ilk adımı bugün at',
    ctaText: 'Ücretsiz deneme dersi için hemen bizimle iletişime geç.',
    ctaButton: { label: 'Ücretsiz Deneme Dersi', href: '/iletisim' },
    description: 'Uzman kadro, 8 kişilik odak grupları ve Türkiye geneli deneme kulübü ile öğrencilerimizi geleceğe en iyi şekilde hazırlıyoruz.',
    quickLinks: [
        { label: 'Anasayfa', href: '/' },
        { label: 'Eğitimler', href: '/egitimler' },
        { label: 'Öğretmenler', href: '/ogretmenler' },
        { label: 'Hakkımızda', href: '/hakkimizda' },
        { label: 'İletişim', href: '/iletisim' },
    ],
    copyrightSuffix: 'Tüm hakları saklıdır.',
};
export const SiteSettingsSchema = z.object({
    brandName: z.string().min(1).max(80).default('Gencay Akademi'),
    contact: ContactInfoSchema.default({ phone: '', email: '', address: '', mapEmbedUrl: '' }),
    social: SocialLinksSchema.default({
        facebook: '',
        instagram: '',
        youtube: '',
        twitter: '',
        whatsapp: '',
    }),
    // Giriş bağlantıları — bizim admin paneline yönlendirir.
    studentLoginUrl: z.string().max(300).default(''),
    teacherLoginUrl: z.string().max(300).default(''),
    seo: SeoSchema.default({ titleSuffix: 'Gencay Akademi', description: '' }),
    // Header navigasyonu + footer (default'lu → geriye dönük uyumlu).
    navigation: SiteNavigationSchema.default(DEFAULT_NAVIGATION),
    footer: SiteFooterSchema.default(DEFAULT_FOOTER),
});
// ---- Section: home (anasayfa) ----
export const HeroSchema = z.object({
    badge: z.string().max(60).default('Özel Ders Merkezi'),
    title: z.string().min(1).max(120),
    subtitle: z.string().max(400).default(''),
    bullets: z.array(z.string().min(1).max(120)).max(6).default([]),
    primaryCta: CtaSchema,
    secondaryCta: CtaSchema,
});
// Hero görselindeki dekoratif cam kartlar (sağ kolon).
export const HeroCardsSchema = z.object({
    satisfactionValue: z.string().max(12).default('%98'),
    satisfactionLabel: z.string().max(40).default('Veli memnuniyeti'),
    chartTitle: z.string().max(40).default('Bu hafta'),
    chartCaption: z.string().max(60).default('Deneme başarı grafiği'),
    chartBars: z.array(z.number().int().min(0).max(100)).max(6).default([70, 88, 64, 95]),
    groupLabel: z.string().max(40).default('8 kişilik gruplar'),
});
const DEFAULT_HERO_CARDS = {
    satisfactionValue: '%98',
    satisfactionLabel: 'Veli memnuniyeti',
    chartTitle: 'Bu hafta',
    chartCaption: 'Deneme başarı grafiği',
    chartBars: [70, 88, 64, 95],
    groupLabel: '8 kişilik gruplar',
};
export const FeatureItemSchema = z.object({
    title: z.string().min(1).max(80),
    text: z.string().min(1).max(240),
    // SVG path 'd' değeri (header/anasayfa ikon seti ile uyumlu).
    icon: z.string().min(1).max(600),
});
export const DenemeKulubuSchema = z.object({
    title: z.string().min(1).max(120),
    description: z.string().max(500).default(''),
    cta: CtaSchema,
});
// İstatistik bandı — animasyonlu sayaç. `value` serbest metin (ör. "1200+",
// "%98", "8 kişi"); web tarafı sayısal kısmı tespit edip sayaç animasyonu yapar.
export const HomeStatSchema = z.object({
    value: z.string().min(1).max(20),
    label: z.string().min(1).max(60),
});
// "Nasıl çalışır" süreç adımı.
export const ProcessStepSchema = z.object({
    title: z.string().min(1).max(80),
    text: z.string().min(1).max(240),
});
export const ProcessSchema = z.object({
    title: z.string().max(120).default('Nasıl Çalışır?'),
    subtitle: z.string().max(300).default(''),
    steps: z.array(ProcessStepSchema).max(6).default([]),
});
// Veli/öğrenci görüşü (testimonial).
export const TestimonialSchema = z.object({
    name: z.string().min(1).max(80),
    role: z.string().max(60).default(''), // ör. "Veli", "12. Sınıf Öğrencisi"
    quote: z.string().min(1).max(500),
});
export const TestimonialsSchema = z.object({
    title: z.string().max(120).default('Velilerimiz ve Öğrencilerimiz Ne Diyor?'),
    subtitle: z.string().max(300).default(''),
    items: z.array(TestimonialSchema).max(12).default([]),
});
// Sıkça sorulan soru.
export const FaqItemSchema = z.object({
    question: z.string().min(1).max(200),
    answer: z.string().min(1).max(800),
});
export const FaqSchema = z.object({
    title: z.string().max(120).default('Sıkça Sorulan Sorular'),
    subtitle: z.string().max(300).default(''),
    items: z.array(FaqItemSchema).max(12).default([]),
});
// Zengin varsayılan içerik — şema seviyesinde tutulur ki anahtarı olmayan
// (eski) DB satırları parse edilince de site dolu görünür ("site asla boş
// kalmaz"). Admin panelden bu bölümleri düzenleyebilir/boşaltabilir.
const HOME_STATS_DEFAULT = [
    { value: '1500+', label: 'Mezun Öğrenci' },
    { value: '%98', label: 'Veli Memnuniyeti' },
    { value: '8', label: 'Kişilik Gruplar' },
    { value: '12', label: 'Yıllık Tecrübe' },
];
const HOME_PROCESS_DEFAULT = {
    title: 'Üç Adımda Başarı Yolculuğu',
    subtitle: 'Tanışmadan ilk başarı grafiğine kadar her adımda yanındayız.',
    steps: [
        {
            title: 'Ücretsiz Tanışma',
            text: 'Seviye tespiti ve ücretsiz deneme dersiyle öğrencinin ihtiyacını birlikte belirliyoruz.',
        },
        {
            title: 'Kişiye Özel Plan',
            text: 'Uygun seviye grubuna yerleştirip hedefe yönelik haftalık çalışma programı oluşturuyoruz.',
        },
        {
            title: 'Takip ve Gelişim',
            text: 'Deneme analizleri ve veli panelindeki anlık raporlarla gelişimi şeffafça izliyoruz.',
        },
    ],
};
const HOME_TESTIMONIALS_DEFAULT = {
    title: 'Velilerimiz ve Öğrencilerimiz Ne Diyor?',
    subtitle: 'Başarı hikâyeleri en güzel onların sözleriyle anlatılır.',
    items: [
        {
            name: 'Ayşe K.',
            role: 'Veli',
            quote: 'Kızımın deneme sonuçlarını panelden anlık görebilmek bana çok güven verdi. Öğretmenlerin ilgisi ve 8 kişilik gruplar gerçekten fark yaratıyor.',
        },
        {
            name: 'Mert A.',
            role: '12. Sınıf Öğrencisi',
            quote: 'Deneme kulübü sayesinde Türkiye genelindeki yerimi görüp eksiklerime odaklandım. Net sayım kısa sürede ciddi arttı.',
        },
        {
            name: 'Selin Y.',
            role: 'Veli',
            quote: 'Hem ödev takibi hem de düzenli geri bildirim sayesinde oğlumun çalışma disiplini tamamen değişti. Gönül rahatlığıyla tavsiye ediyorum.',
        },
    ],
};
const HOME_FAQ_DEFAULT = {
    title: 'Sıkça Sorulan Sorular',
    subtitle: 'Aklındaki soruların yanıtı burada; daha fazlası için bize ulaş.',
    items: [
        {
            question: 'Ücretsiz deneme dersi nasıl alabilirim?',
            answer: 'İletişim formundan veya telefonla bize ulaşman yeterli. Seviye tespiti yapıp uygun grupla ücretsiz bir deneme dersi planlıyoruz.',
        },
        {
            question: 'Gruplar kaç kişiden oluşuyor?',
            answer: 'Tüm gruplarımız en fazla 8 öğrenciden oluşur. Aynı seviyedeki öğrencileri bir araya getirerek odaklı ve verimli bir öğrenme ortamı sağlıyoruz.',
        },
        {
            question: 'Veli olarak çocuğumun gelişimini nasıl takip ederim?',
            answer: 'Veli panelinden derslere katılım, ödev durumu, sınav sonuçları ve başarı grafiğini anlık olarak görüntüleyebilirsin.',
        },
        {
            question: 'Hangi sınavlara hazırlık veriyorsunuz?',
            answer: 'TYT, AYT ve LGS başta olmak üzere ara sınıflara yönelik konu ve yazılı kampları ile online dersler sunuyoruz.',
        },
    ],
};
export const HomeContentSchema = z.object({
    hero: HeroSchema,
    // Hero görselindeki dekoratif kartlar.
    heroCards: HeroCardsSchema.default(DEFAULT_HERO_CARDS),
    // İstatistik bandı (hero altı güven şeridi).
    stats: z.array(HomeStatSchema).max(8).default(HOME_STATS_DEFAULT),
    featuresTitle: z.string().max(120).default('Neden Gencay Akademi?'),
    featuresSubtitle: z.string().max(300).default(''),
    features: z.array(FeatureItemSchema).max(8).default([]),
    // "Nasıl çalışır" süreç bölümü.
    process: ProcessSchema.default(HOME_PROCESS_DEFAULT),
    denemeKulubu: DenemeKulubuSchema,
    // Veli/öğrenci görüşleri.
    testimonials: TestimonialsSchema.default(HOME_TESTIMONIALS_DEFAULT),
    // Sıkça sorulan sorular.
    faq: FaqSchema.default(HOME_FAQ_DEFAULT),
});
// ---- Section: about (Hakkımızda) ----
export const AboutStatSchema = z.object({
    label: z.string().min(1).max(60),
    value: z.string().min(1).max(40),
});
export const AboutContentSchema = z.object({
    eyebrow: z.string().max(60).default('Biz Kimiz'),
    title: z.string().min(1).max(120).default('Hakkımızda'),
    intro: z.string().max(600).default(''),
    body: z.string().max(6000).default(''),
    stats: z.array(AboutStatSchema).max(6).default([]),
    seoTitle: z.string().max(80).default(''),
    seoDescription: z.string().max(320).default(''),
});
// ---- Section: pages (statik sayfa başlık bantları + SEO) ----
// Eğitimler / Öğretmenler / İletişim sayfalarının üst başlık + SEO metinleri.
export const PageMetaSchema = z.object({
    eyebrow: z.string().max(60).default(''),
    title: z.string().min(1).max(120),
    subtitle: z.string().max(300).default(''),
    seoTitle: z.string().max(80).default(''),
    seoDescription: z.string().max(320).default(''),
});
export const PagesContentSchema = z.object({
    courses: PageMetaSchema.default({
        eyebrow: 'Programlar',
        title: 'Eğitimlerimiz',
        subtitle: 'Online dersler, özel dersler ve kamplar — hedefine uygun programı seç.',
        seoTitle: 'Eğitimler',
        seoDescription: 'Gencay Akademi online dersler, özel dersler ve kamplar — hedefine uygun eğitim programını keşfet.',
    }),
    teachers: PageMetaSchema.default({
        eyebrow: 'Kadro',
        title: 'Öğretmen Kadromuz',
        subtitle: 'Alanında uzman, deneme dersleriyle öğrenciler tarafından seçilen eğitmenlerimiz.',
        seoTitle: 'Öğretmen Kadromuz',
        seoDescription: 'Alanında uzman, deneme dersleriyle öğrenciler tarafından seçilen Gencay Akademi eğitmenleri.',
    }),
    contact: PageMetaSchema.default({
        eyebrow: 'İletişim',
        title: 'Bize Ulaşın',
        subtitle: 'Sorularınız için bize yazın, en kısa sürede dönüş yapalım.',
        seoTitle: 'İletişim',
        seoDescription: 'Gencay Akademi ile iletişime geçin — sorularınız için bize ulaşın.',
    }),
});
const PAGES_CONTENT_DEFAULT = {
    courses: {
        eyebrow: 'Programlar',
        title: 'Eğitimlerimiz',
        subtitle: 'Online dersler, özel dersler ve kamplar — hedefine uygun programı seç.',
        seoTitle: 'Eğitimler',
        seoDescription: 'Gencay Akademi online dersler, özel dersler ve kamplar — hedefine uygun eğitim programını keşfet.',
    },
    teachers: {
        eyebrow: 'Kadro',
        title: 'Öğretmen Kadromuz',
        subtitle: 'Alanında uzman, deneme dersleriyle öğrenciler tarafından seçilen eğitmenlerimiz.',
        seoTitle: 'Öğretmen Kadromuz',
        seoDescription: 'Alanında uzman, deneme dersleriyle öğrenciler tarafından seçilen Gencay Akademi eğitmenleri.',
    },
    contact: {
        eyebrow: 'İletişim',
        title: 'Bize Ulaşın',
        subtitle: 'Sorularınız için bize yazın, en kısa sürede dönüş yapalım.',
        seoTitle: 'İletişim',
        seoDescription: 'Gencay Akademi ile iletişime geçin — sorularınız için bize ulaşın.',
    },
};
// ---- Section şema haritası + admin yazma payload'ı ----
export const SiteContentSchemas = {
    settings: SiteSettingsSchema,
    home: HomeContentSchema,
    about: AboutContentSchema,
    pages: PagesContentSchema,
};
/** Admin `PUT /cms/:key` body'si — key'e göre data doğrulanır (route'ta). */
export const UpdateSiteContentSchema = z.object({
    data: z.unknown(),
});
// ---- Varsayılan içerik (DB boşken web + admin fallback) ----
export const DEFAULT_SITE_SETTINGS = SiteSettingsSchema.parse({
    brandName: 'Gencay Akademi',
    contact: {
        phone: '0505 583 84 42',
        email: 'gencayhan40@gmail.com',
        address: 'Bakırköy / İstanbul',
        mapEmbedUrl: '',
    },
    social: {},
    studentLoginUrl: '',
    teacherLoginUrl: '',
    seo: {
        titleSuffix: 'Gencay Akademi',
        description: 'Gencay Akademi — özel ders merkezi. Online dersler, deneme kulübü, yazılı ve konu kampları ile geleceğe hazırlanın.',
    },
});
export const DEFAULT_HOME_CONTENT = HomeContentSchema.parse({
    hero: {
        badge: 'Özel Ders Merkezi · TYT · AYT · LGS',
        title: 'Başarıya giden yolda yanında olan akademi',
        subtitle: 'Uzman kadro, 8 kişilik odak grupları ve Türkiye geneli deneme kulübü ile potansiyelini en üst seviyeye taşı. Hedefin net, yolun bizimle daha kısa.',
        bullets: ['8 kişilik odak grupları', 'Türkiye geneli deneme sıralaması', 'Anlık veli takibi'],
        primaryCta: { label: 'Ücretsiz Deneme Dersi Al', href: '/iletisim' },
        secondaryCta: { label: 'Eğitimleri Keşfet', href: '/egitimler' },
    },
    stats: [
        { value: '1500+', label: 'Mezun Öğrenci' },
        { value: '%98', label: 'Veli Memnuniyeti' },
        { value: '8', label: 'Kişilik Gruplar' },
        { value: '12', label: 'Yıllık Tecrübe' },
    ],
    featuresTitle: 'Neden Gencay Akademi?',
    featuresSubtitle: 'Öğrencilerimizin başarısını önceleyen, teknolojiyle desteklenen bir eğitim deneyimi.',
    features: [
        {
            title: 'Eğitim Paneli',
            text: 'Yoklama, ödevlendirme ve takip, ders & ödev raporları tek panelde.',
            icon: 'M4 5h16v11H4zM2 19h20',
        },
        {
            title: 'Uzman Kadro',
            text: 'Deneme dersleriyle öğrenciler tarafından seçilen uzman öğretmenler.',
            icon: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4 20a8 8 0 0 1 16 0',
        },
        {
            title: 'Deneme Kulübü',
            text: 'Seçkin yayınlardan deneme sınavları ve detaylı analizler.',
            icon: 'M6 4h9l3 3v13H6zM9 9h6M9 13h6',
        },
        {
            title: '8 Kişilik Gruplar',
            text: 'Aynı düzeydeki öğrenciler aynı grupta — başarıyı artıran odak.',
            icon: 'M3 7h18M3 12h18M3 17h18',
        },
    ],
    process: {
        title: 'Üç Adımda Başarı Yolculuğu',
        subtitle: 'Tanışmadan ilk başarı grafiğine kadar her adımda yanındayız.',
        steps: [
            {
                title: 'Ücretsiz Tanışma',
                text: 'Seviye tespiti ve ücretsiz deneme dersiyle öğrencinin ihtiyacını birlikte belirliyoruz.',
            },
            {
                title: 'Kişiye Özel Plan',
                text: 'Uygun seviye grubuna yerleştirip hedefe yönelik haftalık çalışma programı oluşturuyoruz.',
            },
            {
                title: 'Takip ve Gelişim',
                text: 'Deneme analizleri ve veli panelindeki anlık raporlarla gelişimi şeffafça izliyoruz.',
            },
        ],
    },
    denemeKulubu: {
        title: 'Gencay Akademi Deneme Kulübü',
        description: 'TYT & AYT, LGS ve ara sınıflar için seçkin yayınlardan oluşan deneme sınavları, Türkiye geneli sıralama ve detaylı analiz.',
        cta: { label: 'Hemen Katıl', href: '/iletisim' },
    },
    testimonials: {
        title: 'Velilerimiz ve Öğrencilerimiz Ne Diyor?',
        subtitle: 'Başarı hikâyeleri en güzel onların sözleriyle anlatılır.',
        items: [
            {
                name: 'Ayşe K.',
                role: 'Veli',
                quote: 'Kızımın deneme sonuçlarını panelden anlık görebilmek bana çok güven verdi. Öğretmenlerin ilgisi ve 8 kişilik gruplar gerçekten fark yaratıyor.',
            },
            {
                name: 'Mert A.',
                role: '12. Sınıf Öğrencisi',
                quote: 'Deneme kulübü sayesinde Türkiye genelindeki yerimi görüp eksiklerime odaklandım. Net sayım kısa sürede ciddi arttı.',
            },
            {
                name: 'Selin Y.',
                role: 'Veli',
                quote: 'Hem ödev takibi hem de düzenli geri bildirim sayesinde oğlumun çalışma disiplini tamamen değişti. Gönül rahatlığıyla tavsiye ediyorum.',
            },
        ],
    },
    faq: {
        title: 'Sıkça Sorulan Sorular',
        subtitle: 'Aklındaki soruların yanıtı burada; daha fazlası için bize ulaş.',
        items: [
            {
                question: 'Ücretsiz deneme dersi nasıl alabilirim?',
                answer: 'İletişim formundan veya telefonla bize ulaşman yeterli. Seviye tespiti yapıp uygun grupla ücretsiz bir deneme dersi planlıyoruz.',
            },
            {
                question: 'Gruplar kaç kişiden oluşuyor?',
                answer: 'Tüm gruplarımız en fazla 8 öğrenciden oluşur. Aynı seviyedeki öğrencileri bir araya getirerek odaklı ve verimli bir öğrenme ortamı sağlıyoruz.',
            },
            {
                question: 'Veli olarak çocuğumun gelişimini nasıl takip ederim?',
                answer: 'Veli panelinden derslere katılım, ödev durumu, sınav sonuçları ve başarı grafiğini anlık olarak görüntüleyebilirsin.',
            },
            {
                question: 'Hangi sınavlara hazırlık veriyorsunuz?',
                answer: 'TYT, AYT ve LGS başta olmak üzere ara sınıflara yönelik konu ve yazılı kampları ile online dersler sunuyoruz.',
            },
        ],
    },
});
export const DEFAULT_ABOUT_CONTENT = AboutContentSchema.parse({
    eyebrow: 'Biz Kimiz',
    title: 'Hakkımızda',
    intro: 'Gencay Akademi olarak öğrencilerimizi geleceğe en iyi şekilde hazırlamak için çalışıyoruz.',
    body: 'Uzman kadromuz, deneme kulübümüz ve teknolojiyle desteklenen eğitim panelimizle; online dersler, yazılı ve konu kampları sunuyoruz. Aynı düzeydeki öğrencileri 8 kişilik gruplarda buluşturarak başarıyı artırıyoruz.',
    stats: [
        { label: 'Grup Mevcudu', value: '8 kişi' },
        { label: 'Deneme Kulübü', value: 'TYT/AYT/LGS' },
        { label: 'Eğitim Türü', value: 'Online + Kamp' },
    ],
});
export const DEFAULT_PAGES_CONTENT = PagesContentSchema.parse(PAGES_CONTENT_DEFAULT);
/** Public bootstrap yanıtı (web tek istekte alabilir). */
export const PublicSiteSchema = z.object({
    settings: SiteSettingsSchema,
    home: HomeContentSchema,
    pages: PagesContentSchema,
});
// ============================================
// İletişim Mesajları (Faz 4)
// ============================================
// Public form gönderimi (apps/web /iletisim). `website` = honeypot (bot tuzağı,
// boş olmalı). Kullanıcı input'u; .passthrough() YOK.
export const CreateContactMessageSchema = z.object({
    name: z.string().min(1).max(120),
    email: z.string().email().max(160),
    phone: z.string().max(40).optional().or(z.literal('')),
    subject: z.string().max(160).optional().or(z.literal('')),
    message: z.string().min(1).max(4000),
    website: z.string().max(0).optional(), // honeypot — dolu ise bot
});
export const ContactMessageSchema = z.object({
    id: IdSchema,
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    subject: z.string().nullable(),
    message: z.string(),
    readAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const ContactMessageListSchema = z.object({
    items: z.array(ContactMessageSchema),
    total: z.number().int().nonnegative(),
    unread: z.number().int().nonnegative(),
});
//# sourceMappingURL=site.js.map